import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Panel, IPanelProps } from '../../components/Layout/Panel/Panel';
import { IDataProps, IItemProps } from '../../components/Layout/Panel/views/components/TreeBody';
import { ITreeViewProps } from '../../components/Layout/Panel/views/Tree';
import { EIcon } from '../../components/Layout/Panel/views/components/Icon';
import { ISegmentProps, spaceDelimiter } from '../../components/Layout/Panel/views/Segment';
import { AddTypeDialog } from '../../components/Dialog/AddTypeDialog';
import { EditSubTypeDialog } from '../../components/Dialog/EditSubTypeDialog';
import { AddMetalGateSubtypeDialog } from '../../components/Dialog/AddMetalGateSubtypeDialog';
import { useTypesForProject } from './hooks/projects';
import { MetalGateReferenceThumbnail } from './MetalGateReferenceThumbnail';
import {
	SubTypeDataModel,
	SubTypeValueDataModel,
	ProjectToTypesFullDetailsDataModel,
} from '../../models/project.model';
import { deleteProjectToType } from '../../services/project.service';
import DeleteConfirmationDialog from '../../components/Dialog/DeleteConfirmationDialog';

export const TypeSubTypePanel = (props: any) => {
	const [selectedProjectType, setSelectedProjectType] = useState<ProjectToTypesFullDetailsDataModel | null>();
	const [subTypesList, setSubTypesList] = useState<Array<SubTypeDataModel>>([]);
	const [showAddTypeDialog, setShowAddTypeDialog] = useState<boolean>(false);
	const [showEditSubTypeDialog, setShowEditSubTypeDialog] = useState<boolean>(false);
	const [showAddMetalGateSubtypeDialog, setShowAddMetalGateSubtypeDialog] = useState<boolean>(false);
	// Just alternates to trigger reload
	const [toggleForTypeReload, setToggleForTypeReload] = useState<boolean>(false);

	const [showDeleteTypeConfirmation, setShowDeleteTypeConfirmation] = useState<boolean>(false);
	const [typeToDelete, setTypeToDelete] = useState<number | null>(null);

	const onRefreshTypePanel = () => {
		// Purely for refreshing the page
		setToggleForTypeReload(!toggleForTypeReload);
	};

	const projectTypesModel: Array<ProjectToTypesFullDetailsDataModel> | undefined = useTypesForProject(
		props.selectedProject?.id,
		toggleForTypeReload
	);

	const handleDelete = async (projectToTypeId: number) => {
		try {
			const result: any = await deleteProjectToType('project/typeAssociation?projectToTypeId=' + projectToTypeId);

			if (result.data.success) {
				toast.success(result.data.message);
				onRefreshTypePanel();
			} else {
				toast.error(result.data.message);
			}
		} catch (err: any) {
			toast.error(err?.response?.data?.message || err?.message);
		}
		setTypeToDelete(null);
		setShowDeleteTypeConfirmation(false);
	};

	const getTypeData = () => {
		let result = [];

		if (projectTypesModel) {
			const sortedTypes = [...projectTypesModel].sort((a, b) => {
				const nameCmp = (a.typeName || '').localeCompare(b.typeName || '', undefined, { sensitivity: 'base' });
				if (nameCmp !== 0) return nameCmp;
				return (a.clientName || '').localeCompare(b.clientName || '', undefined, { sensitivity: 'base' });
			});

			for (let i = 0; i < sortedTypes.length; i++) {
				result.push({
					text: `${sortedTypes[i].typeName} ${spaceDelimiter}${sortedTypes[i].clientName}`,
					textClickCb: () => {
						setSelectedProjectType(sortedTypes[i]);
					},
					isTextSelected: sortedTypes[i].typeId === selectedProjectType?.typeId,
					icons: [
						{
							icon: EIcon.DELETE,
							iconCb: async () => {
								setTypeToDelete(sortedTypes[i].id);
								setShowDeleteTypeConfirmation(true);
							},
						},
					],
				});
			}
		}

		return result;
	};
	const getAllSubTypes = async () => {
		try {
			if (selectedProjectType) {
				setSubTypesList(selectedProjectType.subTypes);
			}
		} catch (err: any) {
			toast.error(err?.response?.data?.message || err?.message);
		}
	};

	const onAddTypeIconClick = () => {
		setShowAddTypeDialog(true);
	};

	const isMetalGate = selectedProjectType?.typeName?.toLowerCase() === 'metal gate';

	const onEditSubTypeIconClick = () => {
		if (isMetalGate) {
			setShowAddMetalGateSubtypeDialog(true);
		} else {
			setShowEditSubTypeDialog(true);
		}
	};

	const buildMetalGateLabel = (aiFingerprint: any) => {
		if (!aiFingerprint) return 'Pending AI analysis';
		return `${aiFingerprint.type} • ${aiFingerprint.color} • ${aiFingerprint.pattern}`;
	};

	const getSubTypeData = () => {
		let data: Array<IDataProps> = [];

		if (subTypesList?.length) {
			const sortedSubTypes = [...subTypesList].sort((a, b) =>
				(a.subTypeName || '').localeCompare(b.subTypeName || '', undefined, { sensitivity: 'base' })
			);

			sortedSubTypes.map((subType: SubTypeDataModel, index: number) => {
				let subList: Array<IItemProps> = [];
				if (subType.subTypeId) {
					if (subType?.subtypeValuesList?.length) {
						const sortedValues = [...subType.subtypeValuesList].sort((a, b) =>
							(a.value || '').localeCompare(b.value || '', undefined, { sensitivity: 'base' })
						);
						sortedValues.map((subItem: SubTypeValueDataModel, index: number) => {
							subList.push({
								id: subItem.id,
								label: subItem.value!,
								sellingPrice: subItem?.sellingPrice ? subItem?.sellingPrice : 0,
								purchaseCost: subItem?.purchaseCost ? subItem?.purchaseCost : 0,
								leading: isMetalGate ? (
									<MetalGateReferenceThumbnail
										projectToSubTypeId={subItem.projectToSubTypeId}
										hasReferenceImage={subItem.hasReferenceImage}
										label={`${subType.subTypeName} ${subItem.value}`}
									/>
								) : undefined,
							});
						});
					}

					data.push({
						item: {
							id: subType.subTypeId,
							label: subType.subTypeName,
						},
						subItems: [...subList],
					});
				}
			});
		}

		return data;
	};

	const typeProps: ISegmentProps = {
		header: {
			text: `Types${spaceDelimiter}Clients`,
			icons: [
				{
					icon: EIcon.ADD,
					iconCb: () => {
						onAddTypeIconClick();
					},
				},
			],
		},
		placeholderText: 'No types set',
		data: getTypeData(),
	};

	const subTypeProps: ITreeViewProps = {
		header: {
			text: 'Sub Types',
			icons: [
				{
					icon: EIcon.EDIT,
					iconCb: () => {
						onEditSubTypeIconClick();
					},
				},
			],
		},
		placeholderText: 'No sub-types',
		treeData: getSubTypeData(),
	};

	const panelProps: IPanelProps = {
		container: {
			width: props.width ? props.width : '100%',
			height: props.height ? props.height : '100%',
			padding: '8px',
		},
		segments: [typeProps, subTypeProps],
	};

	useEffect(() => {
		if (projectTypesModel?.length) {
			setSelectedProjectType(projectTypesModel[0]);
		} else {
			setSelectedProjectType(null);
			setSubTypesList([]);
		}
	}, [projectTypesModel]);

	useEffect(() => {
		getAllSubTypes();
	}, [selectedProjectType]);

	return (
		<>
			<Panel panelProps={panelProps}></Panel>
			<AddTypeDialog
				selectedProjectId={props.selectedProject?.id}
				show={showAddTypeDialog}
				onRefreshTypePanel={onRefreshTypePanel}
				close={() => {
					setShowAddTypeDialog(false);
				}}></AddTypeDialog>
			{isMetalGate ? (
				<AddMetalGateSubtypeDialog
					selectedProjectType={selectedProjectType}
					show={showAddMetalGateSubtypeDialog}
					onRefreshTypePanel={onRefreshTypePanel}
					close={() => {
						setShowAddMetalGateSubtypeDialog(false);
					}}></AddMetalGateSubtypeDialog>
			) : (
				<EditSubTypeDialog
					selectedProjectId={props.selectedProject?.id}
					selectedProjectType={selectedProjectType}
					selectedSubTypeList={subTypesList}
					show={showEditSubTypeDialog}
					onRefreshTypePanel={onRefreshTypePanel}
					close={() => {
						setShowEditSubTypeDialog(false);
					}}></EditSubTypeDialog>
			)}

			<DeleteConfirmationDialog
				open={showDeleteTypeConfirmation}
				onClose={() => setShowDeleteTypeConfirmation(false)}
				onConfirm={async () => await handleDelete(typeToDelete)}
				entity={'type'}
			/>
		</>
	);
};
